import { useState } from "react";
import { X, Trash2, Plus, Minus, Plane, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useStore } from "@/context/StoreContext";
import { resolveImg, formatPrice } from "@/lib/api";
import { useNavigate } from "react-router-dom";

export default function CartDrawer() {
  const { items, open, setOpen, removeItem, updateQty, total, count, clear } = useCart();
  const { settings } = useStore();
  const [note, setNote] = useState("");
  const navigate = useNavigate();

  const checkout = () => {
    const wa = settings?.whatsapp_number || "919999999999";
    let msg = "✈️ *New PRIYARAA Order*\n\n";
    items.forEach((i, idx) => {
      msg += `${idx + 1}. ${i.name} × ${i.qty} — ${formatPrice(i.price * i.qty)}\n`;
    });
    msg += `\n*Total: ${formatPrice(total)}*\n`;
    if (note.trim()) msg += `\n📝 Destination message: ${note.trim()}\n`;
    msg += `\nPlease confirm availability & delivery. Thank you!`;
    window.open(`https://wa.me/${wa}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70]" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-black/45" onClick={() => setOpen(false)} />
      <div className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-[#FAF7F2] shadow-2xl">
        {/* header */}
        <div className="flex items-center justify-between border-b border-[#E7DFD3] bg-[#2C221E] px-5 py-4 text-cream">
          <div className="flex items-center gap-2">
            <Plane size={18} className="text-amber-400" />
            <h2 className="font-serif text-xl text-[#F4E7C5]">Your Flight Bag</h2>
            <span className="font-mono text-xs text-stone-400">({count})</span>
          </div>
          <button data-testid="close-cart-button" onClick={() => setOpen(false)} className="text-[#F4E7C5]">
            <X size={22} />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
            <ShoppingBag size={48} className="text-stone-300" />
            <p className="font-serif text-2xl text-stone-700">Your bag is ready for boarding</p>
            <p className="text-sm text-stone-500">Add a treasure to begin your journey.</p>
            <button
              data-testid="cart-empty-shop-button"
              onClick={() => {
                setOpen(false);
                navigate("/shop");
              }}
              className="mt-2 rounded-full gold-gradient px-6 py-2.5 text-sm font-medium text-stone-900"
            >
              Explore Collection
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {items.map((i) => (
                <div key={i.id} data-testid={`cart-item-${i.id}`} className="flex gap-3 border-b border-[#EFE8DC] py-4">
                  <img
                    src={resolveImg(i.image_url)}
                    alt={i.name}
                    className="h-20 w-20 rounded-lg object-cover"
                  />
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-start justify-between">
                      <h3 className="font-serif text-base leading-tight text-stone-900">{i.name}</h3>
                      <button
                        data-testid={`remove-item-${i.id}`}
                        onClick={() => removeItem(i.id)}
                        className="text-stone-400 hover:text-red-600"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <p className="mt-0.5 text-sm text-stone-500">{formatPrice(i.price)}</p>
                    <div className="mt-auto flex items-center gap-3">
                      <div className="flex items-center rounded-full border border-[#E7DFD3]">
                        <button
                          data-testid={`decr-${i.id}`}
                          onClick={() => updateQty(i.id, i.qty - 1)}
                          className="px-2 py-1 text-stone-700"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="w-6 text-center font-mono text-sm">{i.qty}</span>
                        <button
                          data-testid={`incr-${i.id}`}
                          onClick={() => updateQty(i.id, i.qty + 1)}
                          className="px-2 py-1 text-stone-700"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <span className="ml-auto font-sans text-sm font-semibold text-stone-900">
                        {formatPrice(i.price * i.qty)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              <div className="mt-4">
                <label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">
                  Add a destination message (optional)
                </label>
                <textarea
                  data-testid="cart-note-input"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  placeholder="Gift note, size, or delivery instructions..."
                  className="mt-1 w-full resize-none rounded-lg border border-[#E7DFD3] bg-white p-3 text-sm outline-none focus:border-amber-500"
                />
              </div>

              <div className="mt-4 flex items-center gap-2 rounded-lg bg-[#F4E7C5]/50 p-3">
                <Plane size={16} className="text-amber-700" />
                <p className="font-mono text-[10px] uppercase tracking-wide text-amber-800">
                  Free signature passport gift packaging included
                </p>
              </div>
            </div>

            {/* footer */}
            <div className="border-t border-[#E7DFD3] bg-white px-5 py-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="font-serif text-lg text-stone-700">Total</span>
                <span data-testid="cart-total" className="font-sans text-xl font-semibold text-stone-900">
                  {formatPrice(total)}
                </span>
              </div>
              <button
                data-testid="proceed-checkout-button"
                onClick={() => { setOpen(false); navigate("/checkout"); }}
                className="flex w-full items-center justify-center gap-2 rounded-full gold-gradient py-3.5 font-semibold uppercase tracking-wider text-stone-900 transition-transform hover:scale-[1.02]"
              >
                <Plane size={18} /> Proceed to Checkout
              </button>
              <button
                data-testid="whatsapp-checkout-button"
                onClick={checkout}
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-full border border-[#25D366] py-2.5 text-sm font-medium text-[#1a9e4b] hover:bg-[#25D366]/10"
              >
                Quick order via WhatsApp
              </button>
              <button
                data-testid="clear-cart-button"
                onClick={clear}
                className="mt-2 w-full text-center text-xs uppercase tracking-widest text-stone-400 hover:text-stone-600"
              >
                Clear bag
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
